// src/utils/stripe.js
// Dynamically picks the Stripe publishable key based on the active mode stored
// in Firestore config/stripeMode (set via the admin dashboard toggle).
// Falls back to REACT_APP_STRIPE_MODE env var if Firestore is unavailable.

import { loadStripe } from '@stripe/stripe-js';

// No hardcoded fallback keys here on purpose: a stale fallback pointing at the
// wrong Stripe account fails silently (PaymentMethod tokens get created against
// account A while the backend charges through account B) instead of erroring
// loudly, which is far worse than just requiring the env vars to be set.
const LIVE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || null;
const TEST_PK = process.env.REACT_APP_STRIPE_TEST_PUBLISHABLE_KEY || null;

let stripePromise = null;
let resolvedMode = null;

const resolveMode = async () => {
  if (resolvedMode !== null) return resolvedMode;

  try {
    // Dynamic import to avoid circular deps
    const { db } = await import('../firebase/config');
    const { doc, getDoc } = await import('firebase/firestore');
    const snap = await getDoc(doc(db, 'config', 'stripeMode'));
    resolvedMode = snap.exists() ? (snap.data().mode || 'live') : (process.env.REACT_APP_STRIPE_MODE || 'live');
  } catch {
    resolvedMode = process.env.REACT_APP_STRIPE_MODE || 'live';
  }

  return resolvedMode;
};

export const getStripe = async () => {
  const mode = await resolveMode();
  const pk = mode === 'test' ? TEST_PK : LIVE_PK;

  if (!pk) {
    const varName = mode === 'test' ? 'REACT_APP_STRIPE_TEST_PUBLISHABLE_KEY' : 'REACT_APP_STRIPE_PUBLISHABLE_KEY';
    throw new Error(`Missing ${varName} — set it to the matching publishable key for the Stripe account configured on the backend (STRIPE_MODE=${mode}).`);
  }

  if (!stripePromise) {
    stripePromise = loadStripe(pk);
  }
  return stripePromise;
};

// Reset cache (called by dashboard when mode changes)
export const resetStripeMode = () => {
  stripePromise = null;
  resolvedMode = null;
};

export default getStripe;
